import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ResourceSearch from '../../components/ui/ResourceSearch';
import { blogService } from '../../services/blogService';

const ManufacturerToolbox = () => {
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [pageSettings, setPageSettings] = useState({
    title: 'Manufacturer Toolbox',
    subtitle: 'Comprehensive manufacturer enablement platform providing regional 3D printing workshops and CNC fabrication labs with essential tools, resources, and guidelines for processing localized component queues.',
    cta_title: 'Ready to scale your production?',
    cta_subtitle: 'Join the manufacturing network on OpenGadget to capture premium regional contracts on-demand.'
  });

  useEffect(() => {
    const loadPageData = async () => {
      try {
        setLoading(true);
        const [posts, cats, settings] = await Promise.all([
          blogService.getPublishedPosts({ targetPage: 'manufacturer-toolbox', limit: 12 }),
          blogService.getCategories(),
          blogService.getPageSettings('manufacturer-toolbox')
        ]);
        setBlogPosts(posts);
        setCategories(cats || []);
        if (settings) setPageSettings(settings);
      } catch (error) {
        console.error('Error fetching manufacturer toolbox data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPageData();
  }, []);
  
  const filteredPosts = activeCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category?.id === activeCategory);

  const toolboxSections = [
    {
      id: 'mfg-guidelines',
      title: 'Production Quality Standards',
      icon: 'CheckCircle',
      description: 'Technical specifications and tolerance thresholds for localized fabrication.',
      features: [
        {
          title: '3D Printing layer height',
          items: [
            'FDM layer heights: 0.1mm - 0.25mm',
            'SLA resolutions: 0.05mm - 0.1mm (liquid resin)',
            'Infill density: 20% - 100% depending on mechanical load',
            'Minimum wall thickness: 1.2mm for structural stability'
          ]
        },
        {
          title: 'CNC Machining Tolerances',
          items: [
            'Standard dimensional tolerance: ±0.125mm',
            'Precision fits tolerance: ±0.025mm',
            'Surface roughness index: Ra 3.2μm standard',
            'Preferred finishes: Bead blasting, Anodizing, or Polishing'
          ]
        }
      ]
    },
    {
      id: 'logistics-shipping',
      title: 'Order Fulfillment & Shipping',
      icon: 'Truck',
      description: 'Standardized guidelines for packaging, labeling, and delivery.',
      features: [
        {
          title: 'Fulfillment Guidelines',
          items: [
            'Parts must be cleaned of support structure residue',
            'Visual surface inspection of all parts prior to packaging',
            'Double-wall corrugated shipping boxes required for heavy parts',
            'Foam-in-place or heavy bubble wrap to prevent sliding damage'
          ]
        },
        {
          title: 'Shipping Label Generation',
          items: [
            'Print shipping labels directly from the Dashboard orders queue',
            'Securely stick labels to the exterior of shipping parcels',
            'Symmetrical tracking code uploads to prevent manual receipt delays',
            'Carrier options: UPS, FedEx, or regional ground courier networks'
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Hero Section */}
      <div className="bg-slate-900 text-white py-24 border-b border-slate-800">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-600 rounded-2xl mb-6 shadow-lg shadow-indigo-500/20">
              <Icon name="Settings" size={40} className="text-white" />
            </div>
            <h1 className="text-5xl font-extrabold mb-6 tracking-tight">{pageSettings.title}</h1>
            <p className="text-xl text-slate-300 mb-10 leading-relaxed font-medium">
              {pageSettings.subtitle}
            </p>
            <div className="flex justify-center gap-4 mb-12">
              <Button
                variant="primary"
                className="bg-indigo-600 hover:bg-indigo-700 text-white border-none px-8 py-3 font-bold"
                onClick={() => navigate('/manufacturer-signup')}
              >
                Apply as Partner
              </Button>
              <Button
                variant="outline"
                className="border-slate-700 text-white hover:bg-slate-800 px-8 py-3"
                onClick={() => navigate('/manufacturer-signup')}
              >
                Explore Requirements
              </Button>
            </div>

            <ResourceSearch placeholder="Search manufacturing resources, specs, guides..." />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-16">
        {/* Resource Center Section */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 text-left">
            <div>
              <h2 className="text-3xl font-bold text-slate-800">Manufacturer Resource Center</h2>
              <p className="text-slate-600 mt-2 text-lg">Insights, guides, and strategies to grow your on-demand localized workshop operations</p>
            </div>
            <Link to="/resources-hub" className="text-indigo-600 hover:text-indigo-700 font-bold flex items-center group bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100 transition-all hover:shadow-md">
              View All Articles
              <Icon name="ArrowRight" size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Category Filter Chips */}
          {!loading && categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              <button
                onClick={() => setActiveCategory('all')}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                  activeCategory === 'all'
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                }`}
              >
                All Topics
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all border ${
                    activeCategory === cat.id
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-2xl h-80 shadow-sm border border-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8 text-left">
              {filteredPosts?.map((post) => (
                <Link
                  key={post.id}
                  to={`/${post.target_page || post.targetPage || 'blog'}/${post.slug}`}
                  className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-all group flex flex-col h-full"
                >
                  {post.image_url && (
                    <div className="h-48 w-full overflow-hidden relative">
                      <img
                        src={post.image_url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest">{post.category?.name || 'Guides'}</span>
                      <h3 className="text-xl font-bold text-slate-800 mt-2 line-clamp-2 group-hover:text-indigo-600 transition-colors">{post.title}</h3>
                      <p className="text-slate-500 text-sm mt-3 line-clamp-3 leading-relaxed">{post.description}</p>
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-50 pt-4 mt-6">
                      <span className="text-xs text-slate-400 font-medium">{post.read_time || post.readTime || '5 min read'}</span>
                      <span className="text-xs font-bold text-indigo-600 group-hover:underline flex items-center">
                        Read Article <Icon name="ChevronRight" size={14} className="ml-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
              {filteredPosts.length === 0 && (
                <div className="col-span-full bg-white p-12 text-center rounded-2xl border border-slate-200 shadow-sm">
                  <Icon name="Inbox" size={48} className="mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500 text-lg font-medium">No resources found under this topic.</p>
                  <p className="text-slate-400 text-sm mt-1">Select another topic above or check back soon!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Informational Guides & Technical Specs Accordion Grid */}
        <div className="grid md:grid-cols-2 gap-12 text-left">
          {toolboxSections.map((section) => (
            <div key={section.id} id={section.id} className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm scroll-mt-24">
              <div className="flex items-center space-x-4 mb-6">
                <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                  <Icon name={section.icon} size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-800">{section.title}</h2>
                  <p className="text-slate-500 text-sm mt-0.5">{section.description}</p>
                </div>
              </div>

              <div className="space-y-6">
                {section.features.map((feature, idx) => (
                  <div key={idx} className="border-t border-slate-100 pt-6 first:border-none first:pt-0">
                    <h4 className="font-bold text-slate-800 text-base mb-3 flex items-center">
                      <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full mr-2"></span>
                      {feature.title}
                    </h4>
                    <ul className="space-y-2.5">
                      {feature.items.map((item, itemIdx) => (
                        <li key={itemIdx} className="text-slate-600 text-sm flex items-start">
                          <Icon name="Check" size={16} className="text-emerald-500 mr-2 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA Banner */}
        <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white p-12 rounded-3xl mt-20 text-center relative overflow-hidden">
          <div className="max-w-2xl mx-auto space-y-6 relative z-10">
            <h3 className="text-3xl font-extrabold tracking-tight">{pageSettings.cta_title}</h3>
            <p className="text-indigo-200">{pageSettings.cta_subtitle}</p>
            <div className="pt-4">
              <Button
                variant="primary"
                className="bg-indigo-600 hover:bg-indigo-700 text-white border-none px-8 py-3.5 font-bold shadow-lg shadow-indigo-600/30"
                onClick={() => navigate('/manufacturer-signup')}
              >
                Join Manufacturing Network
              </Button>
            </div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full filter blur-3xl -mr-20 -mt-20"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full filter blur-3xl -ml-20 -mb-20"></div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ManufacturerToolbox;
